import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { PlateMindLogo } from "@/components/PlateMindLogo";
import { Button } from "@/components/ui/button";
import { LogoutConfirmDialog } from "@/components/ConfirmDialogs";
import {
  LayoutDashboard, Trophy, Users, Zap, LogOut, ChevronRight
} from "lucide-react";

const tabs = [
  { path: "/corporate", label: "Overview", icon: LayoutDashboard },
  { path: "/corporate/programs", label: "Programs", icon: Zap },
  { path: "/corporate/leaderboard", label: "Leaderboard", icon: Trophy },
  { path: "/corporate/employees", label: "Employees", icon: Users },
];

const CorporateLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50/30">
      {/* Top Bar */}
      <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-lg border-b border-emerald-100/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PlateMindLogo size={32} />
            <div>
              <h1 className="text-lg font-bold text-secondary">Wellness Hub</h1>
              <p className="text-xs text-muted-foreground">{user?.organization_name || user?.name}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowLogoutDialog(true)}
            className="text-muted-foreground hover:text-red-500"
            data-testid="corporate-logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="bg-white border-b border-emerald-100/50">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 flex gap-0.5 sm:gap-1 overflow-x-auto" style={{WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
          {tabs.map(({ path, label, icon: Icon }) => {
            const isActive = location.pathname === path;
            return (
              <Link
                key={path}
                to={path}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? "border-emerald-600 text-emerald-700"
                    : "border-transparent text-muted-foreground hover:text-secondary hover:border-emerald-200"
                }`}
                data-testid={`corporate-tab-${label.toLowerCase()}`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {label}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-2 sm:px-6 py-4 sm:py-6">
        <Outlet />
      </main>

      <LogoutConfirmDialog
        isOpen={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        onConfirm={() => { logout(); navigate("/login?type=professional"); }}
      />
    </div>
  );
};

export default CorporateLayout;
